import { useDocPermissions } from "@components/access/use-doc-permissions";
import { useMutation, useOthers, useStorage } from "@liveblocks/react";
import { nanoid } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "@store";
import { updateStoryByKeys } from "@store/story";
import {
  getLastSessionId,
  getStoryId,
  getVersionId,
} from "@store/story/selectors";
import type React from "react";
import { useEffect, useRef } from "react";
import { setCookie } from "@/shared";
import { addCollabBreadcrumb } from "@/shared/utils/sentry-helpers";
import { CollaborationEnabledProvider } from "./CollaborationEnabledProvider";

// A collaborative session ends after this long with nobody in the story; the
// next person to open it then starts a fresh session id (so a "session" no
// longer spans days / multiple sittings).
const SESSION_IDLE_MS = 30 * 60 * 1000;

export const CurrentSessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <CollaborationEnabledProvider fallback={children}>
      <Provider>{children}</Provider>
    </CollaborationEnabledProvider>
  );
};

const Provider = ({ children }: { children: React.ReactNode }) => {
  const lastSessionId = useAppSelector(getLastSessionId);
  const storyId = useAppSelector(getStoryId);
  const versionId = useAppSelector(getVersionId);
  const dispatch = useAppDispatch();
  // Send flow: viewers, commenters and guests get a read-only Liveblocks grant
  // (backend collaboration/room-access.js), and a storage write throws for them
  // ("Cannot write to storage with a read only user"), crashing the editor. They
  // never autosave, so they need no session id.
  const { canEdit } = useDocPermissions();
  const storageLoaded = useStorage((root) => !!root);
  // Kept in a ref so the mutation (deps: []) reads the live peer count without
  // re-subscribing. Presence is real-time, so this reflects who's here now.
  const othersCount = useOthers((others) => others.length);
  const othersCountRef = useRef(othersCount);
  othersCountRef.current = othersCount;

  const updateSession = useMutation(({ storage }) => {
    const sessionKey = `session:${storyId}${versionId ? `:${versionId}` : ""}`;
    const storedSession = storage.get("session") as string | null;
    const lastActivityAt = storage.get("sessionUpdatedAt") as number | null;
    const now = Date.now();

    // Roll the session over only on (re)entry, only after a 30-min idle gap,
    // and only when we're ALONE — if a peer is still in the room the session is
    // genuinely live and must stay shared. `sessionUpdatedAt` is refreshed on
    // every open below, so it tracks when the story was last opened.
    const idleTooLong =
      typeof lastActivityAt === "number" &&
      now - lastActivityAt > SESSION_IDLE_MS;
    const alone = othersCountRef.current === 0;
    const rolledOver = Boolean(storedSession) && idleTooLong && alone;
    const sessionId =
      !storedSession || rolledOver ? nanoid(20) : storedSession;

    if (rolledOver) {
      addCollabBreadcrumb("session rolled over after >30min idle", {
        storyId,
        versionId: versionId ?? null,
      });
    }

    storage.set("session", sessionId);
    storage.set("sessionUpdatedAt", now);
    // broadcast:true → update LOCAL Redux only, do NOT broadcast update-story.
    // This fires on every story open; lastSessionId is derived per-client
    // session state (each peer derives it from the shared Liveblocks `session`),
    // so broadcasting the whole story here both is unnecessary AND created a
    // phantom undo step on load — the autosave/update-story re-assertion is
    // undo-tracked. (Monday #12337353624)
    dispatch(
      updateStoryByKeys({ data: { lastSessionId: sessionId }, broadcast: true }),
    );
    setCookie({ name: sessionKey, value: sessionId, minutes: 30 });
  }, []);

  useEffect(() => {
    if (canEdit && storyId && storageLoaded) {
      updateSession();
    }
  }, [
    canEdit,
    lastSessionId,
    storyId,
    versionId,
    storageLoaded,
    updateSession,
  ]);

  return <>{children}</>;
};
